/* eslint-disable max-depth */
import Cookies from 'js-cookie';
import { call, put, select } from 'redux-saga/effects';

import { adRequestInFlight } from '../../actions';
import { AdLifecycleActions } from '../../constants';
import * as D from '../../deps';
import { getAdList } from '../../services';

import { logAdLifecycleEvent } from './Analytics';

const log = D.Logger.child({ component: 'domains/ads/Chronos' }, true);

export function skipAdOpportunity(action, track, state) {
  // Is there a post Premium Access companion ad? If yes, don't get a new ad

  // for ad-blocked IE11, getAdList request is aborted, but axios does not detect this,
  // which means GET_ADLIST_FAIL is never dispatched. This prevents parent actions
  // from resolving, such as GLOBAL_SKIP_TRACK.
  const isAdBlockedIE11 =
    D.isIE11() && D.getInDomain('application.utils.isAdBlockerDetected')(state);
  if (!action || !action.data) return true;

  const isAudioAd = D.getInDomain('ads.utils.isAudioAd')(state, track);

  return (
    (action.data.suppressAudioAds && action.data.suppressVideoAds) ||
    D.isArtistMessage(track) ||
    getTrackDeliveryType(state, track) === D.Constants.Amp.VoiceTrackDeliveryTypes.BEFORE ||
    isAudioAd ||
    !D.shouldCurrentListenerGetAds(state) ||
    isAdBlockedIE11 ||
    D.isListenerAdFreeSkip(state)
  );
}

function getUserInitiatedTrackEnd(action) {
  if (action.data && action.data.userTimedOut) return false;
  return action.type !== D.AudioPlayerActions.UPDATE;
}

function getNextStationId(action, currentStationId) {
  return action.type === D.Constants.Playback.ActionTypes.SET_OR_CREATE_STATION
    ? action.data.stationId
    : currentStationId;
}

function getIsReplayAction(action) {
  return action.type === D.Constants.UseCases.ActionTypes.REPLAY_TRACK;
}

function getNextTrack(state, track) {
  const stationId = D.getTrackStationId(track);
  const trackIds = D.getInDomain('playback.utils.getPlaylistTrackIds')(state, stationId);
  const index = trackIds.indexOf(track.get('trackToken'));
  if (index < 0 || index + 1 >= trackIds.size) {
    return null;
  }
  return D.getInDomain('playback.utils.getPlaylistTrackById')(state, trackIds.get(index + 1));
}

function getTrackDeliveryType(state, track) {
  if (track && D.isStationVoiceTrack(track)) {
    return track.get('voiceTrackDeliveryType');
  }

  return null;
}

function getNextTrackDeliveryType(state, track) {
  return getTrackDeliveryType(state, getNextTrack(state, track));
}

export function getApiParams(state, track, action) {
  const currentTrackStationId = D.getTrackStationId(track);
  const isCurrentlyPlayingStation = D.Utils.Listener.isStationPlayback(state);
  const currentTrackToken = isCurrentlyPlayingStation ? D.getFromMap(track, 'trackToken') : null;
  const currentTrackType = D.getTrackType(track);
  const userInitiatedTrackEnd = getUserInitiatedTrackEnd(action);
  const userInitiatedTrackEndActionType = action.type;
  const nextTrackStationId = getNextStationId(action, currentTrackStationId);
  // this value is required to be an integer
  const currentTrackSecondsPlayed = Math.round(
    D.getInDomain('playback.utils.getCurrentTrackTime')(state),
  );
  const secondsUntilAd = !userInitiatedTrackEnd
    ? 0
    : D.getInDomain('audioAds.utils.getSecondsUntilAudioAd')(state);
  const secondsSinceDisplayAd = D.getInDomain('displayAds.utils.getSecondsSinceLastDisplayAd')(
    state,
  );
  const testMode = D.getTestMode(state);
  const forceCode = D.getTestAudioAdId(state) || D.getTestVideoAdId(state);
  const skipForReplay = getIsReplayAction(action);
  const adsWizzAdId = D.getInDomain('audioAds.utils.getAdsWizzId')(state);
  const adBlockerEnabled = D.getInDomain('application.utils.isAdBlockerDetected')(state);
  const httpReferrer = Cookies.get('http_referrer');

  const videoAdIndex = D.getInDomain('videoAds.utils.getVideoAdIndex', 1)(state);
  const audioAdIndex = D.getInDomain('audioAds.utils.getAudioAdIndex', 1)(state);

  const userTimedOut = !!(action.data && action.data.userTimedOut);

  const currentTrackDeliveryType = getTrackDeliveryType(state, track);
  const nextTrackDeliveryType = getNextTrackDeliveryType(state, track);
  const isAudioAdsSkippabilityEnabled = D.Utils.Listener.isAudioAdsSkippabilityEnabled(state);
  const uid = D.getInDomain('ads.utils.getUID')(state);

  const deviceProperties = {};
  deviceProperties.applicationVersion = D.Utils.DebugSettings.version();

  if (isAudioAdsSkippabilityEnabled) {
    deviceProperties.audioAdSkipVersion = '1';
  }

  const options = {
    videoAdIndex,
    audioAdIndex,
    // the station id of the next station when changing or the current station
    // id when not changing
    nextTrackStationId,
    // calculated by display ads
    secondsUntilAd,
    // the current station id
    currentTrackStationId,
    // the unique token string for the current track
    currentTrackToken,
    // the internal track type served up with the track data
    currentTrackType,
    adsWizzAdId,
    // Pass if client has adblocker enabled
    adBlockerEnabled,
    // The session's original http referrer to attribute ad traffic to
    httpReferrer,
    currentTrackSecondsPlayed,
    userInitiatedTrackEnd,
    userInitiatedTrackEndActionType,
    secondsSinceDisplayAd,
    // for when the user initiates a replay
    skipForReplay,
    // when timeout CTA is interacted with "Are you still listening?"
    userTimedOut,
    currentTrackDeliveryType,
    nextTrackDeliveryType,
    deviceProperties,
    ...(uid && { uid }),
  };

  if (testMode) {
    // should be one of constants/Ads:ChronosTestModeValues depending on whether or not we are using force codes
    options.testMode = testMode;
  }

  if (forceCode) {
    options.forceCode = forceCode;
  }
  log.trace(options, 'getAdList params');
  return options;
}

export function* getAdsFromResponse(response, action, track) {
  const ads = {};
  if (response && response.ads && response.ads.length) {
    for (let i = 0; i < response.ads.length; ++i) {
      const ad = response.ads[i];
      ad.clientCorrelationId = yield select(D.generateClientCorrelationId);
      if (ad.adType === 'VIDEO_AD') {
        const videoAd = yield call(D.getInDomain('videoAds.utils.getAdListVideoAd'), ad, action);
        if (videoAd) {
          if (ads.videoAd) log.debug('Encountered multiple video ads in response, using latest');
          ads.videoAd = videoAd;
        }
      } else if (ad.adType === 'AUDIO_AD') {
        const audioAd = yield call(
          D.getInDomain('audioAds.utils.getAdListAudioAd'),
          ad,
          action,
          track,
        );
        if (audioAd) {
          if (!ads.audioAds) ads.audioAds = [];
          ads.audioAds.push(audioAd);
        }
      } else log.debug({ ad }, 'Unknown ad type encountered, ignoring');
    }
  }
  return ads;
}

export function* processAdOpportunity(state, track, action) {
  const isUserInStationApsExperiment = yield select(D.Utils.Listener.isAPSPandoraStationEnabled);

  if (isUserInStationApsExperiment) {
    return;
  }

  const response = yield call(getAdList, getApiParams(state, track, action));
  const ads = yield call(getAdsFromResponse, response, action, track);
  // we will pass off control of the situation to the responsible ad type manager
  // we favor video ads to audio ads even if they are both present
  if (ads.videoAd && !(D.Utils.ClientInfo.isElectron() && D.Utils.PageVisibility.isHidden())) {
    yield call(
      D.putAndWait,
      D.getInDomain('videoAds.utils.autoPlayVideoBegin')({
        ad: ads.videoAd,
        action,
      }),
    );
  } else if (ads.audioAds && ads.audioAds.length) {
    yield call(
      D.putAndWait,
      D.getInDomain('audioAds.utils.addAudioAdsToQueue')({
        ads: ads.audioAds,
        action,
      }),
    );
  }
}

export function* adOpportunity(action = {}, track) {
  const triggerAction = action.type;
  const token = D.getFromMap(track, 'trackToken');
  let reason = '';
  const state = yield select();
  const adBlockerEnabled = D.getInDomain('application.utils.isAdBlockerDetected')(state);
  // WEBCLIENT-2247 Temporary disabling these events until we figure out our server capacity issue
  // let error = false;
  // yield call(logAdLifecycleEvent, AdLifecycleActions.AD_LIFECYCLE_AD_OPPORTUNITY_START, {status: true});
  try {
    const nextTrackDeliveryType = getNextTrackDeliveryType(state, track);
    if (
      track &&
      !skipAdOpportunity(action, track, state) &&
      nextTrackDeliveryType !== D.Constants.Amp.VoiceTrackDeliveryTypes.AFTER
    ) {
      yield put(adRequestInFlight(true, token));
      return yield call(processAdOpportunity, state, track, action);
    }
    reason = 'No track or should skip ad opportunity';
    log.debug(reason);
  } catch (e) {
    reason = 'Failed attempt to call getAdList with adBlockerEnabled: ' + adBlockerEnabled;
    // error = true;
    log.info(e, reason);
    yield call(logAdLifecycleEvent, AdLifecycleActions.AD_LIFECYCLE_AD_OPPORTUNITY_ERROR, {
      reason,
      status: false,
      triggerAction,
    });
  } finally {
    // yield call(logAdLifecycleEvent, AdLifecycleActions.AD_LIFECYCLE_AD_OPPORTUNITY_END, {reason, status: !error});
    yield put(adRequestInFlight(false, token));
  }
}

export function* adOpportunityWorker(action) {
  yield call(adOpportunity, action.data.action || action, action.data.track);
}
